import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, RotateCcw, PanelRight, Loader2, AlertCircle, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { commsAPI, errMsg, notifyCommsChanged, STATUS_META } from '../../lib/communicationsAPI';
import { usePolling, useMailboxes } from '../../hooks/useComms';
import { UnmatchedBar } from './UnmatchedBar';
import { Thread } from './Thread';
import { Composer } from './Composer';
import { DetailsPanel } from './DetailsPanel';

export const ConversationDetail = ({ id, staff, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const mailboxes = useMailboxes();
  const endRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(async () => {
    try { const res = await commsAPI.get(id); setData(res.data); setError(null); } catch (err) { setError(errMsg(err, 'Failed to load conversation')); }
  }, [id]);
  usePolling(load, 15000, [id]);
  useEffect(() => { if (data) notifyCommsChanged(); }, [data?.conversation?.id]); // eslint-disable-line
  useEffect(() => {
    const n = (data?.messages.length || 0) + (data?.notes.length || 0);
    if (n !== lastCount.current) { endRef.current?.scrollIntoView({ block: 'end' }); lastCount.current = n; }
  }, [data]);

  const apply = (res, ok) => { setData(res.data); if (ok) toast.success(ok); notifyCommsChanged(); onChanged(); };
  const run = (fn, ok) => fn().then((r) => apply(r, ok)).catch((e) => toast.error(errMsg(e)));
  const markUnread = () => commsAPI.action(id, 'unread').then(() => { toast.success('Marked as unread'); notifyCommsChanged(); onChanged(); onBack(); }).catch((e) => toast.error(errMsg(e)));

  if (error) return <div className="flex-1 flex items-center justify-center text-destructive gap-2 text-sm" data-testid="comms-detail-error"><AlertCircle className="w-4 h-4" />{error}</div>;
  if (!data) return <div className="flex-1 flex items-center justify-center" data-testid="comms-detail-loading"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const c = data.conversation;
  const done = c.status === 'done';

  return (
    <div className="flex flex-1 min-w-0 min-h-0">
    <section className="flex-1 flex flex-col min-w-0 min-h-0" aria-label="Conversation" data-testid="comms-detail">
      <header className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border">
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 md:hidden" onClick={onBack} aria-label="Back to list" data-testid="comms-detail-back"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="min-w-[14rem] flex-1 basis-[16rem]">
          <h2 className="font-headings font-semibold text-base md:text-lg text-foreground truncate flex items-center gap-2" data-testid="comms-detail-subject">
            {c.urgent && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" aria-label="Urgent" />}{c.subject}
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs">
            {data.client ? <Link to={`/clients/${data.client.id}`} className="text-primary hover:underline" data-testid="comms-detail-client">{data.client.display_name}</Link> : <span className="text-muted-foreground">No client</span>}
            <span className={cn('px-1.5 py-0.5 rounded font-medium', STATUS_META[c.status]?.className)} data-testid="comms-detail-status">{STATUS_META[c.status]?.label}</span>
            {data.mailbox && <span className="flex items-center gap-1 text-muted-foreground" data-testid="comms-detail-mailbox"><Mail className="w-3 h-3" />{data.mailbox.label ? `${data.mailbox.label} · ` : ''}{data.mailbox.address}</span>}
          </div>
        </div>
        <Select value={c.assigned_user_id || 'none'} onValueChange={(v) => run(() => commsAPI.assign(id, { assigned_user_id: v === 'none' ? null : v }), 'Assignee updated')}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="comms-assignee-select" aria-label="Assignee"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Unassigned</SelectItem>
            {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
          </SelectContent>
        </Select>
        {done
          ? <Button size="sm" variant="outline" className="h-8" onClick={() => run(() => commsAPI.action(id, 'reopen'), 'Reopened')} data-testid="comms-reopen"><RotateCcw className="w-4 h-4 mr-1.5" />Reopen</Button>
          : <Button size="sm" className="h-8" onClick={() => run(() => commsAPI.action(id, 'done'), 'Marked done')} data-testid="comms-mark-done"><CheckCircle2 className="w-4 h-4 mr-1.5" />Mark done</Button>}
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={markUnread} title="Mark unread" aria-label="Mark unread" data-testid="comms-mark-unread"><Mail className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" className="h-8 2xl:hidden" onClick={() => setShowDetails(true)} data-testid="comms-details-toggle"><PanelRight className="w-4 h-4 mr-1.5" />Details</Button>
      </header>
      <UnmatchedBar data={data} onChanged={() => { load(); onChanged(); }} />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <Thread messages={data.messages} notes={data.notes} onChanged={load} />
        <div ref={endRef} />
      </div>
      <Composer key={`${id}-${mailboxes.length}`} convId={id} staff={staff} conv={c} client={data.client} mailboxes={mailboxes} onSent={() => { load(); onChanged(); }} />
      <Sheet open={showDetails} onOpenChange={setShowDetails}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto p-0" data-testid="comms-details-drawer">
          <SheetHeader className="px-4 pt-4"><SheetTitle>Details</SheetTitle></SheetHeader>
          <DetailsPanel data={data} onUpdated={(d) => { setData(d); onChanged(); }} />
        </SheetContent>
      </Sheet>
    </section>
    <aside className="hidden 2xl:block w-80 shrink-0 overflow-y-auto border-l border-border bg-card" aria-label="Client context" data-testid="comms-context-panel">
      <DetailsPanel data={data} onUpdated={(d) => { setData(d); onChanged(); }} />
    </aside>
    </div>
  );
};
